/**
 * @fileoverview Form state, validation, and handlers for the ContactsData section.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getAreaOptions,
  getGenderOptions,
  getTitleOptions,
} from "../../../data/selectOptions";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { getCompanyFieldValue } from "../../../utils/common";
import { validateField } from "../../../utils/validation";
import { getValidationErrorMessage } from "../../../utils/validationErrorMessages.js";
import {
  ADDITIONAL_CONTACT_PARENT_KEYS,
  EMPTY_CONTACTOS_DEFAULT,
  createEmptyAdditionalContact,
  mapAdditionalContactFromApi,
  parentFieldNameForContactKey,
} from "./contactsDataHelpers";

const usePrimaryContactFieldState = (contactos) => {
  const [selectedTitle, setSelectedTitle] = useState(
    contactos?.[0]?.titulo || null,
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    contactos?.[0]?.telefonoCodigoPais || null,
  );
  const [selectedCellphoneCountryCode, setSelectedCellphoneCountryCode] =
    useState(contactos?.[0]?.celularCodigoPais || null);
  const [showPhone, setShowPhone] = useState(
    contactos?.[0]?.mostrarTelefono || false,
  );
  const [showCellphone, setShowCellphone] = useState(
    contactos?.[0]?.mostrarCelular || false,
  );
  const [showEmail, setShowEmail] = useState(
    contactos?.[0]?.mostrarCorreoElectronico || false,
  );
  const [showExtension, setShowExtension] = useState(
    contactos?.[0]?.mostrarExtension || false,
  );
  const [nombre, setNombre] = useState(contactos?.[0]?.nombre || "");
  const [segundoNombre, setSegundoNombre] = useState(
    contactos?.[0]?.segundoNombre || "",
  );
  const [apellidoPaterno, setApellidoPaterno] = useState(
    contactos?.[0]?.apellidoPaterno || "",
  );
  const [apellidoMaterno, setApellidoMaterno] = useState(
    contactos?.[0]?.apellidoMaterno || "",
  );
  const [telefono, setTelefono] = useState(contactos?.[0]?.telefono || "");
  const [celular, setCelular] = useState(contactos?.[0]?.celular || "");
  const [extension, setExtension] = useState(contactos?.[0]?.extension || "");
  const [correoElectronico, setCorreoElectronico] = useState(
    contactos?.[0]?.correoElectronico || "",
  );
  const [fechaNacimiento, setFechaNacimiento] = useState(
    contactos?.[0]?.fechaNacimiento || "",
  );
  const [mostrarFechaNacimiento, setMostrarFechaNacimiento] = useState(
    contactos?.[0]?.mostrarFechaNacimiento || false,
  );
  const [genero, setGenero] = useState(contactos?.[0]?.genero || 0);
  const [mostrarGenero, setMostrarGenero] = useState(
    contactos?.[0]?.mostrarGenero || false,
  );
  const [area, setArea] = useState(contactos?.[0]?.area || "");
  const [mostrarArea, setMostrarArea] = useState(
    contactos?.[0]?.mostrarArea || false,
  );

  return {
    selectedTitle,
    setSelectedTitle,
    selectedCountryCode,
    setSelectedCountryCode,
    selectedCellphoneCountryCode,
    setSelectedCellphoneCountryCode,
    showPhone,
    setShowPhone,
    showCellphone,
    setShowCellphone,
    showEmail,
    setShowEmail,
    showExtension,
    setShowExtension,
    nombre,
    setNombre,
    segundoNombre,
    setSegundoNombre,
    apellidoPaterno,
    setApellidoPaterno,
    apellidoMaterno,
    setApellidoMaterno,
    telefono,
    setTelefono,
    celular,
    setCelular,
    extension,
    setExtension,
    correoElectronico,
    setCorreoElectronico,
    fechaNacimiento,
    setFechaNacimiento,
    mostrarFechaNacimiento,
    setMostrarFechaNacimiento,
    genero,
    setGenero,
    mostrarGenero,
    setMostrarGenero,
    area,
    setArea,
    mostrarArea,
    setMostrarArea,
  };
};

const usePrimaryContactFields = ({ contactos, onFieldChange }) => {
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1023);
    };

    checkTablet();
    window.addEventListener("resize", checkTablet);

    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  // Initialize validation hook for contact fields
  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    contacto1Nombre: contactos?.[0]?.nombre || "",
    contacto1SegundoNombre: contactos?.[0]?.segundoNombre || "",
    contacto1ApellidoPaterno: contactos?.[0]?.apellidoPaterno || "",
    contacto1ApellidoMaterno: contactos?.[0]?.apellidoMaterno || "",
    contacto1Telefono: contactos?.[0]?.telefono || "",
    contacto1Celular: contactos?.[0]?.celular || "",
    contacto1Extension: contactos?.[0]?.extension || "",
    contacto1CorreoElectronico: contactos?.[0]?.correoElectronico || "",
    contacto1FechaNacimiento: contactos?.[0]?.fechaNacimiento || "",
  });

  const fieldState = usePrimaryContactFieldState(contactos);

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  // Handle validated field changes
  const handleValidatedFieldChange = (fieldName, value) => {
    handleFieldChange(fieldName, value);
    handleInputChange(fieldName, value);
  };

  return {
    isTablet,
    ...fieldState,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
    handleInputChange,
    handleValidatedFieldChange,
  };
};

const additionalContactFieldsToValidate = (contact) => [
  { name: "nombre", value: contact.nombre },
  { name: "segundoNombre", value: contact.segundoNombre },
  { name: "apellidoPaterno", value: contact.apellidoPaterno },
  { name: "apellidoMaterno", value: contact.apellidoMaterno },
  { name: "telefono", value: contact.telefono },
  { name: "celular", value: contact.celular },
  { name: "extension", value: contact.extension },
  { name: "correoElectronico", value: contact.correoElectronico },
  { name: "fechaNacimiento", value: contact.fechaNacimiento },
];

const useAdditionalContactsState = ({
  contactosFromCompany,
  onFieldChange,
  t,
}) => {
  const [additionalContacts, setAdditionalContacts] = useState([]);

  // Validation functions for additional contacts
  const validateAdditionalContactField = (contactIndex, fieldName, value) => {
    const contactNumber = contactIndex + 2; // +2 because index 0 is main contact, and we start from 1
    return validateField(
      value,
      parentFieldNameForContactKey(contactNumber, fieldName),
    );
  };

  const getAdditionalContactFieldValidationState = (
    contactIndex,
    fieldName,
    value,
  ) => {
    const validation = validateAdditionalContactField(
      contactIndex,
      fieldName,
      value,
    );
    return validation.isValid ? "valid" : "error";
  };

  const getAdditionalContactErrorMessage = (contactIndex, fieldName, value) =>
    getValidationErrorMessage(
      t,
      validateAdditionalContactField(contactIndex, fieldName, value),
    );

  // Check if any additional contact has validation errors
  const hasAdditionalContactErrors = () =>
    additionalContacts.some((contact, index) =>
      additionalContactFieldsToValidate(contact).some((field) => {
        const validation = validateAdditionalContactField(
          index,
          field.name,
          field.value,
        );
        return !validation.isValid;
      }),
    );

  // Initialize additional contacts when contactos from API/profile changes — not on every company update
  useEffect(() => {
    const currentContactos = contactosFromCompany;
    if (
      currentContactos &&
      Array.isArray(currentContactos) &&
      currentContactos.length > 1
    ) {
      // If we have existing additional contacts, use them (starting from index 1)
      setAdditionalContacts(
        currentContactos.slice(1).map(mapAdditionalContactFromApi),
      );
    } else {
      // Otherwise initialize with one empty contact
      setAdditionalContacts([createEmptyAdditionalContact(Date.now())]);
    }
  }, [contactosFromCompany]);

  const handleAddContact = () => {
    if (additionalContacts.length < 9) {
      const updatedContacts = [
        ...additionalContacts,
        createEmptyAdditionalContact(Date.now()),
      ];
      setAdditionalContacts(updatedContacts);
    }
  };

  const handleRemoveContact = (index) => {
    const updatedContacts = [...additionalContacts];
    updatedContacts.splice(index, 1);
    setAdditionalContacts(updatedContacts);

    // onFieldChange is (field, value) -> contactoUbicacion[field] = value.
    // Only clear the flat fields for the removed row (contacto2 = first additional). Do not rewrite
    // every other contact or null trailing slots — that was bloating companyUpdates / save payloads.
    if (onFieldChange) {
      const removedSlot = index + 2;
      ADDITIONAL_CONTACT_PARENT_KEYS.forEach((key) => {
        onFieldChange(parentFieldNameForContactKey(removedSlot, key), null);
      });
    }
  };

  // Handler for updating additional contact data
  const handleAdditionalContactChange = (index, field, value) => {
    const updatedContacts = [...additionalContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    setAdditionalContacts(updatedContacts);

    // Notify parent component about the change
    if (onFieldChange) {
      const contactNumber = index + 2;
      onFieldChange(parentFieldNameForContactKey(contactNumber, field), value);
    }
  };

  return {
    additionalContacts,
    getAdditionalContactFieldValidationState,
    getAdditionalContactErrorMessage,
    hasAdditionalContactErrors,
    handleAddContact,
    handleRemoveContact,
    handleAdditionalContactChange,
  };
};

export const useContactsDataForm = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);

  // Only the contactos array reference should drive re-sync — flat fields like contacto2Nombre
  // update `company` without mutating contactos, which was wiping locally added contacts.
  const contactosFromCompany = useMemo(
    () =>
      getCompanyFieldValue(
        company,
        "contactos",
        selectedProfile,
        EMPTY_CONTACTOS_DEFAULT,
      ),
    [company, selectedProfile],
  );

  const contactos = getCompanyFieldValue(
    company,
    "contactos",
    selectedProfile,
    EMPTY_CONTACTOS_DEFAULT,
  );

  const primary = usePrimaryContactFields({ contactos, onFieldChange });
  const additional = useAdditionalContactsState({
    contactosFromCompany,
    onFieldChange,
    t,
  });

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    const totalHasErrors =
      primary.hasErrors || additional.hasAdditionalContactErrors();
    if (onValidationChange && prevHasErrorsRef.current !== totalHasErrors) {
      prevHasErrorsRef.current = totalHasErrors;
      onValidationChange(totalHasErrors);
    }
  }, [
    primary.hasErrors,
    additional.hasAdditionalContactErrors,
    onValidationChange,
  ]);

  const titleOptions = getTitleOptions(t);
  const genderOptions = getGenderOptions(t);
  const areaOptions = getAreaOptions(t);

  return {
    t,
    titleOptions,
    genderOptions,
    areaOptions,
    ...primary,
    ...additional,
  };
};
